<template>
  <v-card class="rounded-xl help-card d-flex flex-column">
    <!-- Fixed header: title + always-visible table of contents -->
    <div class="help-header pa-4 pb-3">
      <v-card-title class="text-h5 text-primary text-center pa-0">
        21 Booster, c'est quoi ?
      </v-card-title>
      <p class="text-center text-medium-emphasis mt-1 mb-3">
        L'expérience ultime de collection de cartes numériques
      </p>
      <div class="help-toc">
        <v-chip
          v-for="s in sections"
          :key="s.key"
          size="small"
          variant="tonal"
          color="primary"
          class="pointer"
          @click="scrollToSection(s.key)"
        >
          <span class="mr-1">{{ s.icon }}</span>{{ s.label }}
        </v-chip>
      </div>
    </div>

    <v-divider></v-divider>

    <!-- Scrollable body -->
    <v-card-text class="help-body">
      <p class="text-body-1 mb-5">
        Entrez dans l'univers de 21 Booster, la plateforme où la passion de la collection rencontre la
        stratégie. Ouvrez des paquets, gérez votre album et gravissez les échelons pour prouver au monde
        que vous êtes le collectionneur n°1.
      </p>

      <!-- 1. Card Coins -->
      <section :ref="el => setSectionRef('coins', el)" class="help-section">
        <v-sheet class="help-sheet pa-4 rounded-lg" border>
          <div class="help-section-head">
            <span class="help-section-icon">💎</span>
            <h3 class="text-h6 text-primary mb-0">Les Card Coins</h3>
          </div>
          <p>Les Card Coins sont le cœur de votre aventure. Ces jetons précieux constituent la monnaie unique de la plateforme.</p>
          <ul class="help-list">
            <li><strong>Utilité :</strong> ils sont indispensables pour acquérir de nouveaux boosters dans la boutique.</li>
            <li><strong>Obtention :</strong> gagnez-en via le casino, le sacrifice de cartes, les codes promos, ou en rechargeant votre solde auprès de l'un de nos vendeurs.</li>
          </ul>
        </v-sheet>
      </section>

      <!-- 2. Boosters -->
      <section :ref="el => setSectionRef('boosters', el)" class="help-section">
        <v-sheet class="help-sheet pa-4 rounded-lg" border>
          <div class="help-section-head">
            <span class="help-section-icon">📦</span>
            <h3 class="text-h6 text-primary mb-0">Les Boosters</h3>
          </div>
          <p>Rendez-vous dans la boutique pour échanger vos Coins contre des Boosters.</p>
          <ul class="help-list">
            <li><strong>Découverte :</strong> chaque ouverture est une surprise. Découvrez les cartes que vous avez obtenues.</li>
            <li><strong>Rareté :</strong> croisez les doigts ! Certaines cartes sont communes, d'autres extrêmement rares. La chance sera-t-elle de votre côté ?</li>
            <li><strong>Les Légendaires :</strong> le sommet de la collection. Elles sont toujours obtenues en variante <strong>Classique</strong> (jamais Silver, Golden ou Foil à l'ouverture) et un booster ne peut jamais en contenir plus d'une. Elles ne se trouvent que dans les boosters : la salle des coffres n'en distribue pas.</li>
          </ul>
          <v-expansion-panels class="mt-3">
            <v-expansion-panel>
              <v-expansion-panel-title>
                <strong>Voir les taux d'apparition</strong>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-table density="compact" class="help-table">
                  <tbody>
                    <tr>
                      <th class="font-weight-bold">Rareté / Variante</th>
                      <th class="font-weight-bold">Classique</th>
                      <th class="font-weight-bold">Silver</th>
                      <th class="font-weight-bold">Golden</th>
                      <th class="font-weight-bold">Foil</th>
                    </tr>
                    <tr>
                      <th class="font-weight-bold">Commune</th>
                      <td>{{ Math.round((settings.rarityDropRates.common / 100) * (settings.typeDropRates.common / 100) * 10000) / 100 }}%</td>
                      <td>{{ Math.round((settings.rarityDropRates.silver / 100) * (settings.typeDropRates.common / 100) * 10000) / 100 }}%</td>
                      <td>{{ Math.round((settings.rarityDropRates.golden / 100) * (settings.typeDropRates.common / 100) * 10000) / 100 }}%</td>
                      <td>{{ Math.round((settings.rarityDropRates.foil / 100) * (settings.typeDropRates.common / 100) * 10000) / 100 }}%</td>
                    </tr>
                    <tr>
                      <th class="font-weight-bold">Inhabituelle</th>
                      <td>{{ Math.round((settings.rarityDropRates.common / 100) * (settings.typeDropRates.uncommon / 100) * 10000) / 100 }}%</td>
                      <td>{{ Math.round((settings.rarityDropRates.silver / 100) * (settings.typeDropRates.uncommon / 100) * 10000) / 100 }}%</td>
                      <td>{{ Math.round((settings.rarityDropRates.golden / 100) * (settings.typeDropRates.uncommon / 100) * 10000) / 100 }}%</td>
                      <td>{{ Math.round((settings.rarityDropRates.foil / 100) * (settings.typeDropRates.uncommon / 100) * 10000) / 100 }}%</td>
                    </tr>
                    <tr>
                      <th class="font-weight-bold">Rare</th>
                      <td>{{ Math.round((settings.rarityDropRates.common / 100) * (settings.typeDropRates.rare / 100) * 10000) / 100 }}%</td>
                      <td>{{ Math.round((settings.rarityDropRates.silver / 100) * (settings.typeDropRates.rare / 100) * 10000) / 100 }}%</td>
                      <td>{{ Math.round((settings.rarityDropRates.golden / 100) * (settings.typeDropRates.rare / 100) * 10000) / 100 }}%</td>
                      <td>{{ Math.round((settings.rarityDropRates.foil / 100) * (settings.typeDropRates.rare / 100) * 10000) / 100 }}%</td>
                    </tr>
                    <tr>
                      <th class="font-weight-bold">Mythique</th>
                      <td>{{ Math.round((settings.rarityDropRates.common / 100) * (settings.typeDropRates.mythic / 100) * 10000) / 100 }}%</td>
                      <td>{{ Math.round((settings.rarityDropRates.silver / 100) * (settings.typeDropRates.mythic / 100) * 10000) / 100 }}%</td>
                      <td>{{ Math.round((settings.rarityDropRates.golden / 100) * (settings.typeDropRates.mythic / 100) * 10000) / 100 }}%</td>
                      <td>{{ Math.round((settings.rarityDropRates.foil / 100) * (settings.typeDropRates.mythic / 100) * 10000) / 100 }}%</td>
                    </tr>
                    <tr>
                      <th class="font-weight-bold">Légendaire</th>
                      <td>{{ legendaryDropRate }}%</td>
                      <td class="text-medium-emphasis">—</td>
                      <td class="text-medium-emphasis">—</td>
                      <td class="text-medium-emphasis">—</td>
                    </tr>
                  </tbody>
                </v-table>
                <p class="text-caption text-medium-emphasis mt-2 mb-0">
                  Une carte légendaire est toujours tirée en variante Classique, et un booster ne peut en contenir qu'une seule au maximum.
                </p>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-sheet>
      </section>

      <!-- 3. Collections -->
      <section :ref="el => setSectionRef('collections', el)" class="help-section">
        <v-sheet class="help-sheet pa-4 rounded-lg" border>
          <div class="help-section-head">
            <span class="help-section-icon">🗂️</span>
            <h3 class="text-h6 text-primary mb-0">Collections, Forge &amp; Cimetière</h3>
          </div>
          <p>L'onglet Collections est votre quartier général. C'est ici que vous admirez votre butin, mais c'est aussi un atelier stratégique pour gérer vos doublons.</p>
          <ul class="help-list">
            <li><strong>Le Cimetière (recyclage) :</strong> vos cartes en double ne sont jamais perdues. « Sacrifiez-les » pour récupérer des Card Coins.</li>
            <li><strong>La Forge (fusion) :</strong> ciblez l'excellence ! Combinez plusieurs exemplaires d'une même carte pour en forger une variante de qualité supérieure.</li>
            <li><strong>L'Extraction :</strong> sélectionnez des cartes foil pour en extraire les âmes et améliorer votre classement (vous gagnez des points chaque fois que votre nombre total d'âmes double).</li>
          </ul>
          <v-expansion-panels class="mt-3">
            <v-expansion-panel>
              <v-expansion-panel-title>
                <strong>Voir les valeurs de sacrifice</strong>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-table density="compact" class="help-table">
                  <tbody>
                    <tr>
                      <th class="font-weight-bold">Rareté / Variante</th>
                      <th class="font-weight-bold">Classique</th>
                      <th class="font-weight-bold">Silver</th>
                      <th class="font-weight-bold">Golden</th>
                      <th class="font-weight-bold">Foil</th>
                    </tr>
                    <tr>
                      <th class="font-weight-bold">Commune</th>
                      <td>{{ settings.rarityCash.common.common }}</td>
                      <td>{{ settings.rarityCash.common.silver }}</td>
                      <td>{{ settings.rarityCash.common.golden }}</td>
                      <td>{{ settings.rarityCash.common.foil }}</td>
                    </tr>
                    <tr>
                      <th class="font-weight-bold">Inhabituelle</th>
                      <td>{{ settings.rarityCash.uncommon.common }}</td>
                      <td>{{ settings.rarityCash.uncommon.silver }}</td>
                      <td>{{ settings.rarityCash.uncommon.golden }}</td>
                      <td>{{ settings.rarityCash.uncommon.foil }}</td>
                    </tr>
                    <tr>
                      <th class="font-weight-bold">Rare</th>
                      <td>{{ settings.rarityCash.rare.common }}</td>
                      <td>{{ settings.rarityCash.rare.silver }}</td>
                      <td>{{ settings.rarityCash.rare.golden }}</td>
                      <td>{{ settings.rarityCash.rare.foil }}</td>
                    </tr>
                    <tr>
                      <th class="font-weight-bold">Mythique</th>
                      <td>{{ settings.rarityCash.mythic.common }}</td>
                      <td>{{ settings.rarityCash.mythic.silver }}</td>
                      <td>{{ settings.rarityCash.mythic.golden }}</td>
                      <td>{{ settings.rarityCash.mythic.foil }}</td>
                    </tr>
                    <tr>
                      <th class="font-weight-bold">Légendaire</th>
                      <td>{{ legendaryCash.common }}</td>
                      <td>{{ legendaryCash.silver }}</td>
                      <td>{{ legendaryCash.golden }}</td>
                      <td>{{ legendaryCash.foil }}</td>
                    </tr>
                  </tbody>
                </v-table>
                <p class="text-caption text-medium-emphasis mt-2 mb-0">
                  Les variantes Silver, Golden et Foil d'une carte légendaire ne s'obtiennent qu'à la forge, jamais à l'ouverture d'un booster.
                </p>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-title>
                <strong>Voir les valeurs d'extraction</strong>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <p class="mb-2">Nombre d'âmes obtenues par extraction (uniquement cartes foil) :</p>
                <ul class="help-list">
                  <li><strong>Commune :</strong> {{ settings.raritySoul.common }} âmes</li>
                  <li><strong>Inhabituelle :</strong> {{ settings.raritySoul.uncommon }} âmes</li>
                  <li><strong>Rare :</strong> {{ settings.raritySoul.rare }} âmes</li>
                  <li><strong>Mythique :</strong> {{ settings.raritySoul.mythic }} âmes</li>
                  <li><strong>Légendaire :</strong> {{ legendarySoul }} âmes</li>
                </ul>
                <p class="text-caption text-medium-emphasis mt-2 mb-0">
                  Une carte légendaire devant d'abord être montée en Foil à la forge pour être extraite, l'extraction d'une légendaire demande de la patience… mais rapporte gros.
                </p>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-title>
                <strong>Voir les ratios de conversion de la forge</strong>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <ul class="help-list">
                  <li><strong>Upgrade :</strong> {{ settings.upgradeCost }} pour 1</li>
                  <li><strong>Downgrade :</strong> {{ settings.downgradeCost }} pour 1</li>
                </ul>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-sheet>
      </section>

      <!-- 4. Casino -->
      <section :ref="el => setSectionRef('casino', el)" class="help-section">
        <v-sheet class="help-sheet pa-4 rounded-lg" border>
          <div class="help-section-head">
            <span class="help-section-icon">🎰</span>
            <h3 class="text-h6 text-primary mb-0">Casino</h3>
          </div>
          <p>Vous avez quelques Card Coins qui traînent dans votre inventaire ou vous vous sentez particulièrement chanceux aujourd'hui ? C'est le moment de passer à l'action !</p>
          <p class="mb-0">Rien de plus simple : une fois par jour, pour seulement 50 Card Coins, lancez la roue et découvrez immédiatement la récompense qui vous attend.</p>
        </v-sheet>
      </section>

      <!-- 5. Salle des Coffres -->
      <section :ref="el => setSectionRef('treasure', el)" class="help-section">
        <v-sheet class="help-sheet pa-4 rounded-lg" border>
          <div class="help-section-head">
            <span class="help-section-icon">🎲</span>
            <h3 class="text-h6 text-primary mb-0">Salle des Coffres</h3>
          </div>
          <p>Tous les jours à minuit, la salle des coffres est réinitialisée. Tentez votre chance pour peut-être trouver le coffre légendaire parmi les six.</p>
          <p>Chaque tirage est composé de six coffres. Parmi eux se trouvent :</p>
          <ul class="help-list">
            <li><strong>1 coffre légendaire :</strong> contient 3 cartes plutôt rares dont au moins une carte gold ou foil.</li>
            <li><strong>2 coffres pleins :</strong> contiennent 3 cartes plutôt communes dont au moins une carte silver, gold ou foil.</li>
            <li><strong>2 coffres légers :</strong> contiennent une carte plutôt commune.</li>
            <li><strong>1 coffre vide :</strong> ne contient strictement rien.</li>
          </ul>
          <p>Si un coffre contient plusieurs cartes, elles seront toujours de la même collection. Seules les collections publiques peuvent être gagnées au tirage des coffres.</p>
          <p>Les coffres sélectionnés ont chacun un design qui leur est propre, tiré parmi une liste de designs prédéfinis. Le design d'un coffre est totalement aléatoire et n'est lié en aucun cas à son contenu.</p>
          <p class="mb-0">Pour participer, payez {{ treasureAccessPrice }} Card Coins pour débloquer l'accès au tirage du jour. Vous pourrez ensuite dépenser {{ treasureHintPrice }} Card Coins supplémentaires par coffre pour obtenir un indice. Les indices ne sont pas obligatoires : vous pouvez aussi faire confiance uniquement à votre chance.</p>
        </v-sheet>
      </section>

      <!-- 6. Codes -->
      <section :ref="el => setSectionRef('codes', el)" class="help-section">
        <v-sheet class="help-sheet pa-4 rounded-lg" border>
          <div class="help-section-head">
            <span class="help-section-icon">🎁</span>
            <h3 class="text-h6 text-primary mb-0">Espace Codes</h3>
          </div>
          <p>Vous avez déniché un code secret ou reçu un cadeau ?</p>
          <p class="mb-0">Entrez-le dans la section dédiée pour débloquer des récompenses immédiates : boosters gratuits, cartes exclusives, nouvelles collections ou stocks de Card Coins. Restez à l'affût !</p>
        </v-sheet>
      </section>

      <!-- 7. Échanges -->
      <section :ref="el => setSectionRef('trades', el)" class="help-section">
        <v-sheet class="help-sheet pa-4 rounded-lg" border>
          <div class="help-section-head">
            <span class="help-section-icon">🤝</span>
            <h3 class="text-h6 text-primary mb-0">Échanges</h3>
          </div>
          <p>La section Échanges vous permet de proposer des échanges de cartes entre joueurs.</p>
          <ul class="help-list">
            <li><strong>Création :</strong> chaque nouvelle session consomme 1 ticket d'échange.</li>
            <li><strong>Ticket :</strong> un ticket d'échange coûte {{ tradeTicketPrice }} Card Coins dans la boutique.</li>
            <li><strong>Validation :</strong> le joueur invité accepte ou refuse la demande.</li>
            <li><strong>Équilibrage :</strong> le système compare la valeur de sacrifice des offres. L'écart maximum autorisé est de {{ tradeMaxGapPercent }}% de la plus grosse offre.</li>
            <li><strong>Double confirmation :</strong> les deux joueurs doivent valider l'échange. Toute modification d'offre remet les validations à zéro.</li>
          </ul>
        </v-sheet>
      </section>

      <!-- 8. Compétition -->
      <section :ref="el => setSectionRef('competition', el)" class="help-section">
        <v-sheet class="help-sheet pa-4 rounded-lg" border>
          <div class="help-section-head">
            <span class="help-section-icon">🏆</span>
            <h3 class="text-h6 text-primary mb-0">Compétition &amp; Progression</h3>
          </div>
          <p>21 Booster n'est pas qu'une collection, c'est une compétition.</p>
          <ul class="help-list">
            <li><strong>Les Succès :</strong> relevez des défis spécifiques pour débloquer des bonus et afficher vos prouesses sur votre profil.</li>
            <li><strong>Vos Statistiques :</strong> accédez à des données détaillées sur votre parcours. Partagez-les pour impressionner vos amis.</li>
            <li><strong>Le Classement :</strong> comparez-vous à l'élite. Vous gagnez des points en collectionnant de nouvelles cartes, en complétant des collections entières et en validant des succès.</li>
          </ul>
          <p class="mb-0"><strong>Objectif :</strong> atteindre le sommet et devenir la Légende de 21 Booster.</p>
        </v-sheet>
      </section>

      <!-- Plus de Coins -->
      <section :ref="el => setSectionRef('more-coins', el)" class="help-section">
        <v-sheet class="help-sheet help-sheet--accent pa-4 rounded-lg" border>
          <div class="help-section-head">
            <span class="help-section-icon">🛒</span>
            <h3 class="text-h6 text-primary mb-0">Besoin de plus de Coins ?</h3>
          </div>
          <p class="mb-0">Si vous êtes à court de ressources pour continuer votre collection, cliquez simplement sur votre solde de Card Coins afin d'être mis en relation avec l'un de nos vendeurs.</p>
        </v-sheet>
      </section>

      <!-- CGV -->
      <section :ref="el => setSectionRef('cgv', el)" class="help-section">
        <v-sheet class="help-sheet pa-4 rounded-lg" border>
          <div class="help-section-head">
            <span class="help-section-icon">📜</span>
            <h3 class="text-h6 text-primary mb-0">Conditions Générales de Vente</h3>
          </div>
          <p class="mb-3">Retrouvez l'ensemble des conditions encadrant les achats sur 21 Booster.</p>
          <v-btn color="primary" variant="outlined" @click="showCgvModal = true">
            Accéder aux CGV
          </v-btn>
        </v-sheet>
      </section>
    </v-card-text>

    <v-divider></v-divider>

    <v-card-actions class="justify-center">
      <v-btn color="primary" variant="flat" @click="$emit('close')">J'ai compris</v-btn>
    </v-card-actions>

    <v-dialog v-model="showCgvModal" max-width="700">
      <v-card class="rounded-xl">
        <v-card-title class="text-h6 text-primary">CGV</v-card-title>
        <v-card-text class="text-body-2 cgv-content">
          <p class="mb-2"><strong>Version du :</strong> 3 février 2026</p>
          <p>
            Les présentes Conditions Générales de Vente, ci-après les « CGV », encadrent les achats effectués sur l’application
            21Booster, notamment l’achat de boosters numériques, cartes numériques, Card Coins, âmes, tickets et tout autre asset
            numérique.
          </p>

          <ol class="pl-5">
            <li>
              <h4 class="text-subtitle-2 mb-1">Propriété du site</h4>
              <p>
                Le site et l’application sont la propriété exclusive de Maurice Bohr.
              </p>
              <p>
                Le site est mis à disposition de l’entreprise 21Booster à titre gracieux. Cette mise à disposition ne transfère aucun
                droit de propriété à 21Booster.
              </p>
              <p>
                Maurice Bohr conserve l’entière propriété du site, de son infrastructure, de ses éléments techniques, graphiques et
                fonctionnels.
              </p>
            </li>
            <li>
              <h4 class="text-subtitle-2 mb-1">Exploitation commerciale</h4>
              <p>
                L’entreprise 21Booster dispose du droit d’utilisation du site et bénéficie de l’exclusivité concernant l’ensemble des
                ventes d’assets numériques dans l’application.
              </p>
              <p>
                Toute vente d’assets réalisée via le site ou l’application est effectuée exclusivement par ou pour le compte de 21Booster.
              </p>
            </li>
            <li>
              <h4 class="text-subtitle-2 mb-1">Nature des achats</h4>
              <p>
                Les achats effectués sur 21Booster sont assimilés à une prestation de service numérique consistant à donner accès, pour
                une durée indéterminée, à certains assets numériques tels que cartes, boosters, Card Coins, âmes, tickets ou éléments
                similaires.
              </p>
              <p>
                Ces assets sont exclusivement numériques. Ils ne constituent pas des biens physiques, ne confèrent aucun droit de
                propriété réel, n’ont aucune valeur monétaire garantie et ne sont ni échangeables contre de l’argent, ni remboursables.
              </p>
              <p>L’accès accordé est personnel, limité, révocable et lié au compte utilisateur.</p>
            </li>
            <li>
              <h4 class="text-subtitle-2 mb-1">Boosters et contenu aléatoire</h4>
              <p>Les boosters numériques peuvent contenir des éléments attribués de manière aléatoire.</p>
              <p>
                L’utilisateur reconnaît que l’achat d’un booster ne garantit pas l’obtention d’un asset précis. Aucun remboursement,
                échange ou compensation ne pourra être exigé au motif que le contenu obtenu ne correspond pas aux attentes de
                l’utilisateur.
              </p>
            </li>
            <li>
              <h4 class="text-subtitle-2 mb-1">Prix et paiement</h4>
              <p>Les prix sont indiqués sur le site au moment de l’achat.</p>
              <p>
                La commande est validée après confirmation du paiement. Une fois le paiement confirmé, les assets numériques sont
                attribués au compte de l’utilisateur selon les modalités prévues par l'entreprise 21Booster.
              </p>
            </li>
            <li>
              <h4 class="text-subtitle-2 mb-1">Droit de rétractation</h4>
              <p>
                Compte tenu de la nature numérique des services fournis, l’utilisateur accepte que l’exécution de la prestation commence
                immédiatement après l’achat.
              </p>
              <p>
                En validant sa commande, l’utilisateur reconnaît expressément renoncer à son droit de rétractation dès lors que l’accès
                aux assets numériques est fourni immédiatement. Cette clause doit être acceptée clairement avant paiement, conformément aux
                règles applicables aux contenus numériques fournis sans support matériel.
              </p>
            </li>
            <li>
              <h4 class="text-subtitle-2 mb-1">Suspension, retrait ou révocation des accès</h4>
              <p>
                Le propriétaire du site, Maurice Bohr, se réserve le droit de suspendre, limiter, couper ou interrompre tout ou partie du
                site ou des services, à tout moment, sans préavis ni justification.
              </p>
              <p>
                Les membres de l’entreprise 21Booster et/ou le propriétaire du site se réservent également le droit de révoquer, retirer,
                modifier ou limiter tout ou partie des accès aux assets numériques attribués à un utilisateur, sans préavis ni justification.
              </p>
              <p>
                L’utilisateur reconnaît que l’accès aux assets numériques est accordé pour une durée indéterminée, sans garantie de
                maintien permanent.
              </p>
            </li>
            <li>
              <h4 class="text-subtitle-2 mb-1">Compte utilisateur</h4>
              <p>
                L’utilisateur est responsable de son compte, de ses identifiants et de toute activité réalisée depuis celui-ci.
              </p>
              <p>
                Toute tentative de fraude, exploitation de faille, revente non autorisée, duplication, modification ou détournement
                d’assets numériques peut entraîner la suspension du compte et la perte des accès associés.
              </p>
            </li>
            <li>
              <h4 class="text-subtitle-2 mb-1">Disponibilité du service</h4>
              <p>Le site et l’application sont fournis en l’état.</p>
              <p>
                Aucune garantie n’est donnée quant à une disponibilité continue, sans erreur ou sans interruption. Des opérations de
                maintenance, mises à jour, corrections ou interruptions peuvent intervenir à tout moment.
              </p>
            </li>
            <li>
              <h4 class="text-subtitle-2 mb-1">Responsabilité</h4>
              <p>
                Maurice Bohr et 21Booster ne pourront être tenus responsables des pertes indirectes, pertes de données, pertes d’accès
                temporaires, indisponibilités, erreurs d’affichage ou interruptions du service.
              </p>
            </li>
            <li>
              <h4 class="text-subtitle-2 mb-1">Modification des CGV</h4>
              <p>Les présentes CGV peuvent être modifiées à tout moment.</p>
              <p>
                La version applicable est celle disponible sur le site au moment de l’utilisation du service (en utilisant les services, l'utilisateur est considéré comme ayant pris connaissance des présentes conditions générales et comme les ayant acceptées).
              </p>
            </li>
          </ol>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn color="primary" variant="text" @click="showCgvModal = false">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  props: {
    settings: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showCgvModal: false,
      sectionRefs: {},
      sections: [
        { key: 'coins', icon: '💎', label: 'Card Coins' },
        { key: 'boosters', icon: '📦', label: 'Boosters' },
        { key: 'collections', icon: '🗂️', label: 'Collections' },
        { key: 'casino', icon: '🎰', label: 'Casino' },
        { key: 'treasure', icon: '🎲', label: 'Salle des Coffres' },
        { key: 'codes', icon: '🎁', label: 'Codes' },
        { key: 'trades', icon: '🤝', label: 'Échanges' },
        { key: 'competition', icon: '🏆', label: 'Compétition' },
        { key: 'more-coins', icon: '🛒', label: 'Plus de Coins' },
        { key: 'cgv', icon: '📜', label: 'CGV' }
      ]
    }
  },
  emits: ['close'],
  computed: {
    legendaryDropRate() {
      const raw = Number(this.settings?.typeDropRates?.legendary)
      const value = Number.isFinite(raw) && raw >= 0 ? raw : 0
      return Math.round(value * 100) / 100
    },
    legendaryCash() {
      const cash = this.settings?.rarityCash?.legendary || {}
      return {
        common: Number(cash.common) || 0,
        silver: Number(cash.silver) || 0,
        golden: Number(cash.golden) || 0,
        foil: Number(cash.foil) || 0
      }
    },
    legendarySoul() {
      const raw = Number(this.settings?.raritySoul?.legendary)
      return Number.isFinite(raw) && raw >= 0 ? raw : 0
    },
    tradeTicketPrice() {
      const raw = Number(this.settings?.tradeTicketPrice)
      return Number.isFinite(raw) && raw >= 0 ? raw : 500
    },
    tradeMaxGapPercent() {
      const raw = Number(this.settings?.tradeMaxGapPercent)
      return Number.isFinite(raw) && raw >= 0 ? raw : 10
    },
    treasureAccessPrice() {
      const raw = Number(this.settings?.treasureAccessPrice)
      return Number.isFinite(raw) && raw >= 0 ? raw : 50
    },
    treasureHintPrice() {
      const raw = Number(this.settings?.treasureHintPrice)
      return Number.isFinite(raw) && raw >= 0 ? raw : 25
    }
  },
  methods: {
    setSectionRef(key, el) {
      if (el) this.sectionRefs[key] = el
    },
    scrollToSection(key) {
      const el = this.sectionRefs[key]
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }
}
</script>
